import { isUntargetedDispatchReactivationAllowed } from "../utils/dispatchReassignmentMode.js";

function normalizeInteractionText(value) {
  return typeof value === "string" ? value.trim() : "";
}

function isDompeurOverrideTargetCompatibility(targetCompatibility = null) {
  const reasonCode = normalizeInteractionText(targetCompatibility?.reason_code || targetCompatibility?.reasonCode).toLowerCase();
  const confirmationCode = normalizeInteractionText(targetCompatibility?.confirmation_code || targetCompatibility?.confirmationCode).toLowerCase();
  return reasonCode === "dompeur_override_required"
    || (
      targetCompatibility?.requires_confirmation === true
      && confirmationCode === "dompeur_override"
    );
}

export default function useResultsWorkspaceInteractions({
  canApplyDispatchSourceChanges = false,
  canCloseDispatchDay = false,
  canInvalidateDispatchDay = false,
  canResetDispatchRevalidation = false,
  canManageDispatcherFreeze = false,
  canManageRouteAlternatives = false,
  canPublishDriverPortal = false,
  canMutatePlanning = false,
  canReopenDispatchDay = false,
  canRestoreDispatchSnapshot = false,
  canSaveDispatchSnapshot = false,
  canResetHereRouteCache = false,
  canResetPlanning = false,
  canDeleteDispatchSnapshot = false,
  canRunOptimization = false,
  isAccessLoading = false,
  closeReassignmentPopover,
  detailViewState,
  handleClientSelection,
  mapStateRef,
  onChangeDispatchMonth,
  onCloseDispatchDaySuccess,
  onDispatchSnapshotResetSuccess,
  onProtectAfterReassignmentChange,
  onReassignmentRebuildModeChange,
  onReopenDispatchDaySuccess,
  onSelectDispatchDay,
  onToggleDispatchArchive,
  openErrorsPanel,
  planningMutation,
  portalPublications = null,
  protectAfterReassignment = true,
  reassignmentRebuildMode = "complete",
  refreshPortalPublications = null,
  renderCurrentSelection,
  toggleReassignmentPopover,
  tripSelection,
}) {
  const handleOpenErrorsPanel = ({ errorId = "", filter = "", openAll = false, source = "", viewName = "errors" } = {}) => {
    openErrorsPanel?.({
      errorId,
      filter,
      openAll,
      scroll: true,
      source,
      viewName,
    });
  };

  return {
    assignmentCard: {
      canMutatePlanning: canMutatePlanning === true,
      canPublishDriverPortal: canPublishDriverPortal === true,
      portalPublications,
      reassignmentRebuildMode,
      onCloseReassignment() {
        closeReassignmentPopover({ rerender: false });
      },
      onCommitReassignment({
        allowReturnToOptimized = false,
        compatibility = null,
        deliveryId = "",
        dispatchPeriod = "day",
        expectedRuntimeRevision = "",
        forcedPreloadedPreviousDayOrderIds = [],
        isUndo = false,
        orderIds = [],
        rebuildMode = "",
        onMutationError = null,
        onMutationStart = null,
        onMutationSuccess = null,
        skipConfirmation = false,
        targetTruckId = "",
        targetTruckName = "",
      } = {}) {
        const resolvedTargetTruckId = compatibility?.truck_id || targetTruckId;
        if (!deliveryId || !resolvedTargetTruckId) {
          return;
        }

        closeReassignmentPopover({ rerender: false });
        planningMutation.requestDeliveryMove({
          allowReturnToOptimized,
          deliveryId,
          dispatchPeriod,
          expectedRuntimeRevision,
          forcedPreloadedPreviousDayOrderIds,
          isUndo,
          orderIds,
          ...(rebuildMode ? { rebuildMode } : {}),
          onMutationError,
          onMutationStart,
          onMutationSuccess,
          ...(skipConfirmation ? { skipConfirmation: true } : {}),
          ...(compatibility ? { targetCompatibility: compatibility } : {}),
          targetTruckId: resolvedTargetTruckId,
          targetTruckName: compatibility?.truck_name || targetTruckName || compatibility?.truck_id || resolvedTargetTruckId,
        });
      },
      onCommitGroupReassignment({
        allowReturnToOptimized = false,
        compatibility = null,
        deliveryId = "",
        deliveryIds = [],
        dispatchPeriod = "day",
        expectedRuntimeRevision = "",
        forcedPreloadedPreviousDayOrderIds = [],
        groupStepIds = [],
        isUndo = false,
        orderIds = [],
        rebuildMode = "",
        onMutationError = null,
        onMutationStart = null,
        onMutationSuccess = null,
        skipConfirmation = false,
        sourceGroupKey = "",
        sourceTruckId = "",
        targetTruckId = "",
        targetTruckName = "",
      } = {}) {
        const resolvedDeliveryIds = (Array.isArray(deliveryIds) ? deliveryIds : [])
          .filter((item) => typeof item === "string" && item.trim())
          .map((item) => item.trim());
        const resolvedTargetTruckId = compatibility?.truck_id || targetTruckId;
        if (resolvedDeliveryIds.length < 2 || !resolvedTargetTruckId) {
          if (deliveryId && resolvedTargetTruckId) {
            closeReassignmentPopover({ rerender: false });
            planningMutation.requestDeliveryMove({
              allowReturnToOptimized,
              deliveryId,
              dispatchPeriod,
              expectedRuntimeRevision,
              forcedPreloadedPreviousDayOrderIds,
              isUndo,
              orderIds,
              ...(rebuildMode ? { rebuildMode } : {}),
              onMutationError,
              onMutationStart,
              onMutationSuccess,
              ...(skipConfirmation ? { skipConfirmation: true } : {}),
              ...(compatibility ? { targetCompatibility: compatibility } : {}),
              targetTruckId: resolvedTargetTruckId,
              targetTruckName: compatibility?.truck_name || targetTruckName || compatibility?.truck_id || resolvedTargetTruckId,
            });
          }
          return;
        }

        closeReassignmentPopover({ rerender: false });
        planningMutation.requestDeliveryGroupMove({
          allowReturnToOptimized,
          deliveryIds: resolvedDeliveryIds,
          dispatchPeriod,
          expectedRuntimeRevision,
          forcedPreloadedPreviousDayOrderIds,
          isUndo,
          ...(rebuildMode ? { rebuildMode } : {}),
          onMutationError,
          onMutationStart,
          onMutationSuccess,
          ...(skipConfirmation ? { skipConfirmation: true } : {}),
          ...(compatibility ? { targetCompatibility: compatibility } : {}),
          groupStepIds,
          ...(Array.isArray(orderIds) && orderIds.length > 0 ? { orderIds } : {}),
          sourceGroupKey,
          sourceTruckId,
          targetTruckId: resolvedTargetTruckId,
          targetTruckName: compatibility?.truck_name || targetTruckName || compatibility?.truck_id || resolvedTargetTruckId,
        });
      },
      onClientHoverEnd() {
        mapStateRef.current?.clearHighlight?.();
      },
      onClientHoverStart(clientId) {
        mapStateRef.current?.highlightClient?.(clientId);
      },
      onSelectAssignment(assignmentId) {
        closeReassignmentPopover({ rerender: false });
        tripSelection.selectAssignment(assignmentId);
      },
      onSelectClient(selection) {
        closeReassignmentPopover({ rerender: false });
        handleClientSelection(selection);
      },
      onReleaseDelivery(deliveryId, { dispatchPeriod = "" } = {}) {
        closeReassignmentPopover({ rerender: false });
        planningMutation.releaseDelivery(deliveryId, { dispatchPeriod });
      },
      onReleaseDeliveryGroup({
        deliveryIds = [],
        dispatchPeriod = "",
        groupStepIds = [],
        sourceGroupKey = "",
      }) {
        closeReassignmentPopover({ rerender: false });
        planningMutation.releaseDeliveryGroup({
          deliveryIds,
          dispatchPeriod,
          groupStepIds,
          sourceGroupKey,
        });
      },
      onUnconfirmDeliveryGroup({
        deliveryIds = [],
        groupStepIds = [],
        sourceGroupKey = "",
      }) {
        closeReassignmentPopover({ rerender: false });
        planningMutation.unconfirmDeliveryGroup({
          deliveryIds,
          groupStepIds,
          sourceGroupKey,
        });
      },
      onConfirmDeliveryGroup(payload = {}) {
        closeReassignmentPopover({ rerender: false });
        planningMutation.confirmDeliveryGroup?.(payload);
      },
      onFreezeDelivery: canManageDispatcherFreeze === true
        ? (deliveryId) => {
          closeReassignmentPopover({ rerender: false });
          planningMutation.freezeDelivery?.(deliveryId);
        }
        : undefined,
      onFreezeDeliveryGroup: canManageDispatcherFreeze === true
        ? (payload = {}) => {
          closeReassignmentPopover({ rerender: false });
          planningMutation.freezeDeliveryGroup?.(payload);
        }
        : undefined,
      onOptimizeTruckRoutes(payload = {}) {
        closeReassignmentPopover({ rerender: false });
        planningMutation.optimizeTruckRoutes?.(payload);
      },
      onRecalculateTruckRoute(payload = {}) {
        closeReassignmentPopover({ rerender: false });
        planningMutation.recalculateTruckRoute?.(payload);
      },
      onReassignAllTruckOrders(payload = {}) {
        closeReassignmentPopover({ rerender: false });
        planningMutation.reassignTruckOrders?.(payload);
      },
      onSetTruckPreloadTime(payload = {}) {
        closeReassignmentPopover({ rerender: false });
        return planningMutation.setTruckPreloadTime?.(payload);
      },
      onCreateTruckIncident: canMutatePlanning === true
        ? (payload = {}) => {
          closeReassignmentPopover({ rerender: false });
          return planningMutation.createTruckIncident?.(payload);
        }
        : undefined,
      onUpdateTruckIncident: canMutatePlanning === true
        ? (payload = {}) => {
          closeReassignmentPopover({ rerender: false });
          return planningMutation.updateTruckIncident?.(payload);
        }
        : undefined,
      onDeleteTruckIncident: canMutatePlanning === true
        ? (payload = {}) => {
          closeReassignmentPopover({ rerender: false });
          return planningMutation.deleteTruckIncident?.(payload);
        }
        : undefined,
      onPublishTruckPortal: canPublishDriverPortal === true
        ? (route) => {
          closeReassignmentPopover({ rerender: false });
          planningMutation.publishDispatchDayPortal?.({
            dispatchDate: portalPublications?.dispatchDate,
            dispatchPeriod: portalPublications?.dispatchPeriod,
            routes: route ? [route] : [],
          });
        }
        : undefined,
      onUnpublishTruckPortal: canPublishDriverPortal === true
        ? (route) => {
          closeReassignmentPopover({ rerender: false });
          planningMutation.unpublishDispatchDayPortal?.({
            dispatchDate: portalPublications?.dispatchDate,
            dispatchPeriod: portalPublications?.dispatchPeriod,
            routes: route ? [route] : [],
          });
        }
        : undefined,
      onUpdateTruckDriverName(payload = {}) {
        closeReassignmentPopover({ rerender: false });
        return planningMutation.updateTruckDriverName?.(payload);
      },
      onUpdateTruckTractor(payload = {}) {
        closeReassignmentPopover({ rerender: false });
        return planningMutation.updateTruckTractor?.(payload);
      },
      onUpdateTruckStartTime(payload = {}) {
        closeReassignmentPopover({ rerender: false });
        planningMutation.updateTruckStartTime?.(payload);
      },
      onUpdateTruckTimeSettings(payload = {}) {
        closeReassignmentPopover({ rerender: false });
        planningMutation.updateTruckTimeSettings?.(payload);
      },
      onToggleTruckDispatchDisabled(payload = {}) {
        closeReassignmentPopover({ rerender: false });
        planningMutation.toggleTruckDispatchDisabled?.(payload);
      },
      onReactivateDeliveryToTruck({
        deliveryId = "",
        dispatchPeriod = "day",
        expectedRuntimeRevision = "",
        isUndo = false,
        onMutationError = null,
        onMutationStart = null,
        onMutationSuccess = null,
        orderIds = [],
        rebuildMode = "",
        skipConfirmation = false,
        targetCompatibility = null,
        targetTruckId = "",
        targetTruckName = "",
      } = {}) {
        closeReassignmentPopover({ rerender: false });
        planningMutation.reactivateDelivery(deliveryId, {
          ...(dispatchPeriod === "evening" ? { dispatchPeriod: "evening" } : {}),
          ...(expectedRuntimeRevision ? { expectedRuntimeRevision } : {}),
          ...(isUndo ? { isUndo: true } : {}),
          ...(typeof onMutationError === "function" ? { onMutationError } : {}),
          ...(typeof onMutationStart === "function" ? { onMutationStart } : {}),
          ...(typeof onMutationSuccess === "function" ? { onMutationSuccess } : {}),
          ...(Array.isArray(orderIds) && orderIds.length > 0 ? { orderIds } : {}),
          ...(rebuildMode ? { rebuildMode } : {}),
          ...(skipConfirmation ? { skipConfirmation: true } : {}),
          ...(targetCompatibility ? { targetCompatibility } : {}),
          targetTruckId,
          targetTruckName,
        });
      },
      onSetSegmentBuffer(payload = {}) {
        closeReassignmentPopover({ rerender: false });
        planningMutation.setSegmentBuffer?.(payload);
      },
      onAssignEndOfDayLoad(payload = {}) {
        closeReassignmentPopover({ rerender: false });
        const { eveningTruckId, groupStepIds, sourceDispatchPeriod, sourceGroupKey, sourceTruckName, ...endOfDayLoad } = payload;
        if (sourceDispatchPeriod !== "evening") {
          planningMutation.requestEndOfDayLoadAssignment?.(payload);
          return;
        }
        // Segment du soir : la commande revient d'abord au jour sur le camion source, puis le Chargé PM part de ce camion.
        // Le Chargé PM attend la fin du transfert : une mutation encore en cours refuse la suivante.
        planningMutation.requestEveningStandardTransfer?.({
          deliveryId: endOfDayLoad.deliveryId,
          deliveryIds: endOfDayLoad.deliveryIds,
          dispatchDate: endOfDayLoad.dispatchDate,
          groupStepIds,
          onMutationSuccess: () => window.setTimeout(() => planningMutation.requestEndOfDayLoadAssignment?.({
            ...endOfDayLoad,
            returnedToDayTruckLabel: sourceTruckName,
          }), 0),
          scope: endOfDayLoad.scope,
          sourceDispatchPeriod,
          sourceGroupKey,
          sourceTruckId: eveningTruckId,
          targetPeriod: "day",
          targetTruckId: endOfDayLoad.sourceTruckId,
          targetTruckName: sourceTruckName,
        });
      },
      onAssignEveningStandard(payload = {}) {
        closeReassignmentPopover({ rerender: false });
        planningMutation.requestEveningStandardTransfer?.(payload);
      },
      onAssignPreloadedPreviousDay(payload = {}) {
        closeReassignmentPopover({ rerender: false });
        planningMutation.requestPreloadedPreviousDayAssignment?.(payload);
      },
      onAssignSuspendedPreloadedPreviousDay({
        deliveryId = "",
        deliveryIds = [],
        orderIds = [],
        sourceGroupKey = "",
        targetCompatibility = null,
        targetTruckId = "",
        targetTruckName = "",
      } = {}) {
        const resolvedDeliveryIds = (Array.isArray(deliveryIds) ? deliveryIds : [])
          .filter((item) => typeof item === "string" && item.trim())
          .map((item) => item.trim());
        const resolvedOrderIds = (Array.isArray(orderIds) ? orderIds : [])
          .filter((item) => typeof item === "string" && item.trim())
          .map((item) => item.trim())
          .filter((item, index, items) => items.indexOf(item) === index);
        const primaryDeliveryId = deliveryId || resolvedDeliveryIds[0] || "";
        if (!targetTruckId || !primaryDeliveryId || resolvedOrderIds.length === 0) {
          return;
        }

        closeReassignmentPopover({ rerender: false });
        const requiresDompeurOverrideConfirmation = isDompeurOverrideTargetCompatibility(targetCompatibility);
        planningMutation.requestDeliveryGroupMove?.({
          confirmDompeurOverride: !requiresDompeurOverrideConfirmation,
          deliveryIds: resolvedDeliveryIds.length > 0 ? resolvedDeliveryIds : [primaryDeliveryId],
          forcedPreloadedPreviousDayOrderIds: resolvedOrderIds,
          skipConfirmation: !requiresDompeurOverrideConfirmation,
          sourceGroupKey,
          ...(requiresDompeurOverrideConfirmation ? { targetCompatibility } : {}),
          targetTruckId,
          targetTruckName,
        });
      },
      onRemovePreloadedPreviousDay(payload = {}) {
        closeReassignmentPopover({ rerender: false });
        planningMutation.requestPreloadedPreviousDayRemoval?.(payload);
      },
      onRemoveEndOfDayLoad(payload = {}) {
        closeReassignmentPopover({ rerender: false });
        planningMutation.requestEndOfDayLoadRemoval?.(payload);
      },
      onApplyRouteAlternative: canManageRouteAlternatives
        ? (payload = {}) => {
          closeReassignmentPopover({ rerender: false });
          planningMutation.applyRouteAlternative?.(payload);
        }
        : undefined,
      onRemoveRouteAlternative: canManageRouteAlternatives
        ? (payload = {}) => {
          closeReassignmentPopover({ rerender: false });
          planningMutation.removeRouteAlternative?.(payload);
        }
        : undefined,
      onCommitSegmentReorder(payload = {}) {
        closeReassignmentPopover({ rerender: false });
        planningMutation.requestSegmentReorder?.(payload);
      },
      onUpdateOrderDetails(payload = {}) {
        closeReassignmentPopover({ rerender: false });
        planningMutation.updateOrderDetails?.(payload);
      },
      onSuspendDelivery(deliveryId, {
        dispatchPeriod = "day",
        expectedRuntimeRevision = "",
        isUndo = false,
        onMutationError = null,
        onMutationStart = null,
        onMutationSuccess = null,
        skipConfirmation = false,
      } = {}) {
        closeReassignmentPopover({ rerender: false });
        planningMutation.suspendDelivery(deliveryId, {
          ...(dispatchPeriod === "evening" ? { dispatchPeriod: "evening" } : {}),
          ...(expectedRuntimeRevision ? { expectedRuntimeRevision } : {}),
          ...(isUndo ? { isUndo: true } : {}),
          ...(typeof onMutationError === "function" ? { onMutationError } : {}),
          ...(typeof onMutationStart === "function" ? { onMutationStart } : {}),
          ...(typeof onMutationSuccess === "function" ? { onMutationSuccess } : {}),
          ...(skipConfirmation ? { skipConfirmation: true } : {}),
        });
      },
      onSuspendDeliveryGroup(payload = {}) {
        closeReassignmentPopover({ rerender: false });
        planningMutation.suspendDeliveryGroup?.(payload);
      },
      onUnfreezeDelivery: canManageDispatcherFreeze === true
        ? (deliveryId) => {
          closeReassignmentPopover({ rerender: false });
          planningMutation.unfreezeDelivery?.(deliveryId);
        }
        : undefined,
      onUnfreezeDeliveryGroup: canManageDispatcherFreeze === true
        ? (payload = {}) => {
          closeReassignmentPopover({ rerender: false });
          planningMutation.unfreezeDeliveryGroup?.(payload);
        }
        : undefined,
      onToggleTruckOrdersProtection(payload = {}) {
        closeReassignmentPopover({ rerender: false });
        planningMutation.toggleTruckOrdersProtection?.(payload);
      },
      onToggleTruckOvertime(payload = {}) {
        closeReassignmentPopover({ rerender: false });
        planningMutation.toggleTruckOvertime?.(payload);
      },
      onToggleTruckDompeurMixedLoad(payload = {}) {
        closeReassignmentPopover({ rerender: false });
        planningMutation.toggleTruckDompeurMixedLoad?.(payload);
      },
      onToggleTruckProtection(payload = {}) {
        closeReassignmentPopover({ rerender: false });
        planningMutation.toggleTruckProtection?.(payload);
      },
      onToggleAssignment: tripSelection.toggleAssignmentOpen,
      onToggleDeliveryConfirm: planningMutation.toggleDeliveryConfirm,
      onToggleLoadingConfirm: planningMutation.toggleLoadingConfirm,
      onToggleReassignment: toggleReassignmentPopover,
    },
    chrome: {
      isAccessLoading: isAccessLoading === true,
      canApplyDispatchSourceChanges: canApplyDispatchSourceChanges === true,
      canCloseDispatchDay: canCloseDispatchDay === true,
      canInvalidateDispatchDay: canInvalidateDispatchDay === true,
      canResetDispatchRevalidation: canResetDispatchRevalidation === true,
      canMutatePlanning: canMutatePlanning === true,
      canPublishDriverPortal: canPublishDriverPortal === true,
      canRestoreDispatchSnapshot: canRestoreDispatchSnapshot === true,
      canSaveDispatchSnapshot: canSaveDispatchSnapshot === true,
      canDeleteDispatchSnapshot: canDeleteDispatchSnapshot === true,
      canResetOptimizationRuntimeCache: canResetHereRouteCache === true,
      canReopenDispatchDay: canReopenDispatchDay === true,
      canResetHereRouteCache: canResetHereRouteCache === true,
      canResetPlanning: canResetPlanning === true,
      canRunOptimization: canRunOptimization === true,
      protectAfterReassignment,
      onProtectAfterReassignmentChange,
      reassignmentRebuildMode,
      onReassignmentRebuildModeChange,
      portalPublications,
      onPublishPortalRoutes: canPublishDriverPortal === true
        ? (routes = []) => {
          closeReassignmentPopover({ rerender: false });
          planningMutation.publishDispatchDayPortal?.({
            dispatchDate: portalPublications?.dispatchDate,
            dispatchPeriod: portalPublications?.dispatchPeriod,
            routes,
          });
        }
        : undefined,
      onRefreshPortalPublications: typeof refreshPortalPublications === "function"
        ? refreshPortalPublications
        : undefined,
      onUnpublishPortalRoutes: canPublishDriverPortal === true
        ? (routes = []) => {
          closeReassignmentPopover({ rerender: false });
          planningMutation.unpublishDispatchDayPortal?.({
            dispatchDate: portalPublications?.dispatchDate,
            dispatchPeriod: portalPublications?.dispatchPeriod,
            routes,
          });
        }
        : undefined,
      onCloseDispatchDay: canCloseDispatchDay === true
        ? (dispatchDate) => {
          closeReassignmentPopover({ rerender: false });
          planningMutation.closeDispatchDay?.(dispatchDate, {
            onMutationSuccess() {
              onCloseDispatchDaySuccess?.(dispatchDate);
            },
          });
        }
        : undefined,
      onOpenError: handleOpenErrorsPanel,
      onSuspendOrderFromError: canMutatePlanning === true
        ? (entry) => {
          closeReassignmentPopover({ rerender: false });
          planningMutation.suspendOrderFromError?.(entry);
        }
        : undefined,
      onSuspendErrorGroupFromError: canMutatePlanning === true
        ? (entries) => {
          closeReassignmentPopover({ rerender: false });
          planningMutation.suspendErrorGroupFromErrors?.(entries);
        }
        : undefined,
      onOpenDetailView(viewName, options = {}) {
        closeReassignmentPopover({ rerender: false });
        detailViewState.activateView(viewName, {
          historyMode: "replace",
          scroll: true,
          syncLocation: true,
          ...options,
        });
      },
      onPlanningReset: canResetPlanning === true
        ? (options = {}) => {
          closeReassignmentPopover({ rerender: false });
          planningMutation.requestPlanningReset(options);
        }
        : undefined,
      onDeleteDispatchSnapshot: canDeleteDispatchSnapshot === true
        ? (dispatchDate) => {
          closeReassignmentPopover({ rerender: false });
          planningMutation.deleteDispatchDaySnapshot?.(dispatchDate, {
            onMutationSuccess(context = {}) {
              onDispatchSnapshotResetSuccess?.({
                ...context,
                dispatchDate,
              });
            },
          });
        }
        : undefined,
      onResetOptimizationRuntimeCache: canResetHereRouteCache === true
        ? () => {
          closeReassignmentPopover({ rerender: false });
          planningMutation.resetOptimizationRuntimeCache?.();
        }
        : undefined,
      onInvalidateDispatchDay: canInvalidateDispatchDay === true
        ? (dispatchDate) => {
          closeReassignmentPopover({ rerender: false });
          planningMutation.invalidateDispatchDay?.(dispatchDate);
        }
        : undefined,
      onResetDispatchRevalidation: canResetDispatchRevalidation === true
        ? (dispatchDate) => {
          closeReassignmentPopover({ rerender: false });
          planningMutation.resetDispatchDayRevalidation?.(dispatchDate);
        }
        : undefined,
      onResetHereRouteCache: canResetHereRouteCache === true
        ? () => {
          closeReassignmentPopover({ rerender: false });
          planningMutation.resetHereRouteCache?.();
        }
        : undefined,
      onReopenDispatchDay: canReopenDispatchDay === true
        ? (dispatchDate) => {
          closeReassignmentPopover({ rerender: false });
          planningMutation.reopenDispatchDay?.(dispatchDate, {
            onMutationSuccess() {
              return onReopenDispatchDaySuccess?.(dispatchDate);
            },
          });
        }
        : undefined,
      onToggleDetailView(viewName, options = {}) {
        closeReassignmentPopover({ rerender: false });
        detailViewState.toggleView(viewName, {
          historyMode: "replace",
          scroll: true,
          syncLocation: true,
          ...options,
        });
      },
      onSelectDispatchDay(dispatchDate) {
        closeReassignmentPopover({ rerender: false });
        onSelectDispatchDay?.(dispatchDate);
      },
      onChangeDispatchMonth(dispatchMonth) {
        closeReassignmentPopover({ rerender: false });
        onChangeDispatchMonth?.(dispatchMonth);
      },
      onToggleDispatchArchive(isOpen) {
        closeReassignmentPopover({ rerender: false });
        onToggleDispatchArchive?.(isOpen);
      },
      onReactivateDelivery: isUntargetedDispatchReactivationAllowed(reassignmentRebuildMode)
        ? (deliveryId, {
          dispatchPeriod = "day",
          expectedRuntimeRevision = "",
          onMutationSuccess = null,
          orderIds = [],
        } = {}) => {
          closeReassignmentPopover({ rerender: false });
          planningMutation.reactivateDelivery(deliveryId, {
            ...(dispatchPeriod === "evening" ? { dispatchPeriod: "evening" } : {}),
            ...(expectedRuntimeRevision ? { expectedRuntimeRevision } : {}),
            ...(typeof onMutationSuccess === "function" ? { onMutationSuccess } : {}),
            ...(Array.isArray(orderIds) && orderIds.length > 0 ? { orderIds } : {}),
          });
        }
        : undefined,
      onReactivateDeliveryToTruck({
        deliveryId = "",
        dispatchPeriod = "day",
        expectedRuntimeRevision = "",
        isUndo = false,
        onMutationError = null,
        onMutationStart = null,
        onMutationSuccess = null,
        orderIds = [],
        rebuildMode = "",
        targetCompatibility = null,
        targetTruckId = "",
        targetTruckName = "",
      } = {}) {
        closeReassignmentPopover({ rerender: false });
        planningMutation.reactivateDelivery(deliveryId, {
          ...(dispatchPeriod === "evening" ? { dispatchPeriod: "evening" } : {}),
          ...(expectedRuntimeRevision ? { expectedRuntimeRevision } : {}),
          ...(isUndo ? { isUndo: true } : {}),
          ...(typeof onMutationError === "function" ? { onMutationError } : {}),
          ...(typeof onMutationStart === "function" ? { onMutationStart } : {}),
          ...(typeof onMutationSuccess === "function" ? { onMutationSuccess } : {}),
          ...(Array.isArray(orderIds) && orderIds.length > 0 ? { orderIds } : {}),
          ...(rebuildMode ? { rebuildMode } : {}),
          ...(targetCompatibility ? { targetCompatibility } : {}),
          targetTruckId,
          targetTruckName,
        });
      },
      onRouteClientSelect(selection) {
        closeReassignmentPopover({ rerender: false });
        if (selection?.routeSegmentIndex != null) {
          mapStateRef.current?.highlightRouteSegment?.(selection.routeSegmentIndex);
        }
        handleClientSelection(selection);
      },
      onRouteSequenceSelect(routeSelection) {
        closeReassignmentPopover({ rerender: false });

        const controller = mapStateRef.current;

        if (routeSelection?.restoreView === true) {
          controller?.restoreView?.();
          return;
        }

        if (routeSelection?.routeSegmentIndex != null) {
          controller?.highlightRouteSegment?.(routeSelection.routeSegmentIndex);
        }

        if (controller?.toggleRouteNumber?.(routeSelection?.routeNumber) === true) {
          return;
        }

        controller?.zoomToRouteNumber?.(routeSelection?.routeNumber);
      },
      onRouteHoverEnd() {
        mapStateRef.current?.clearHighlight?.();
      },
      onRouteSegmentHoverStart(routeSegmentIndex, routeSegmentIndexes = []) {
        const controller = mapStateRef.current;
        if (Array.isArray(routeSegmentIndexes) && routeSegmentIndexes.length && controller?.highlightRouteSegments) {
          controller.highlightRouteSegments(routeSegmentIndexes);
          return;
        }
        controller?.highlightRouteSegment?.(routeSegmentIndex);
      },
    },
  };
}
